/**
 * Faz 6 — geometric constraint command runners (Y1).
 *
 * Every GC command: pick entities (+ click locations) -> resolve OCS
 * markers -> record the constraint as ONE undoable step -> re-solve the
 * affected subsystems. Solve updates are silent/history-free (the watcher
 * pattern); only the constraint record itself is a user command.
 */

import * as THREE from 'three';
import { useAppStore, type Entity } from '../../../state/store';
import { useConstraintStore, type ConstraintInput } from '../../constraints/constraintStore';
import { undoableConstraints } from '../../constraints/constraintOps';
import { solveConstraintSet, syncBoundDimensions, type SolveResult } from '../../constraints/solve';
import { inferAutoConstrain } from '../../constraints/autoconstrain';
import { KIND_ACCEPT_TYPES } from '../../constraints/kinds';
import {
    nearestWritableMarker,
    nearestPolylineSegment,
    particleMarkers,
    resolveMarkerPoint,
} from '../../constraints/attach';
import { MARKER_CENTER, MARKER_RADIUS, type ConstraintRef } from '../../constraints/types';
import { runInteraction, PK, PLoopK } from './lib';
import type { RunnerMap } from '../runnerRegistry';
import { getRegisteredAction } from '../runnerRegistry';
import type { Pt3 } from '../geometryMath';

const RADIAL_TYPES = ['circle', 'arc', 'ellipse'];
const LINE_TYPES = ['line', 'polyline', 'polygon'];

/** Turn a pick (entity + world click location) into a writable marker ref. */
function pickRefAt(picks: string[], pickPoints: THREE.Vector3[], index: number): { ref: ConstraintRef; point: Pt3 } | null {
    const { entities } = useAppStore.getState();
    const entity = entities[picks[index]];
    if (!entity) return null;
    const wp = pickPoints[index];
    const world: [number, number] = wp
        ? [wp.x, wp.y]
        : (() => {
            const any = resolveMarkerPoint(entity, MARKER_CENTER);
            return any ? [any[0], any[1]] : [0, 0];
        })();
    const hit = nearestWritableMarker(entity, world);
    if (!hit) return null;
    return { ref: { entityId: entity.id, marker: hit.marker }, point: hit.point };
}

/**
 * Endpoint pair of the clicked segment (line: both ends; polyline: the
 * picked stroke's vertices) — the ref shape of H/V/Parallel/Angle kinds.
 */
function segmentRefsOf(entity: Entity, world: [number, number]): ConstraintRef[] | null {
    const mk = (marker: number): ConstraintRef => ({ entityId: entity.id, marker });
    if (entity.type === 'line') return [mk(0), mk(1)];
    if (entity.type === 'polyline' || entity.type === 'polygon') {
        const seg = nearestPolylineSegment(entity, world);
        if (seg !== null) {
            const vs = (Array.isArray((entity.data as Record<string, any>).vertices) ? (entity.data as Record<string, any>).vertices : []) as Pt3[];
            return [mk(seg), mk((seg + 1) % Math.max(1, vs.length))];
        }
    }
    return null;
}

/** Record constraint inputs as ONE undoable step, then re-solve. */
function commitConstraints(label: string, inputs: ConstraintInput[]): { ids: string[]; result: SolveResult } {
    let ids: string[] = [];
    undoableConstraints(() => {
        ids = useConstraintStore.getState().addConstraints(inputs);
    }, label);
    const result = solveConstraintSet();
    if (result.converged || result.conflicts.length > 0) syncBoundDimensions();
    return { ids, result };
}

function reportGc(cmd: string, kind: string, ids: string[], result: SolveResult): string {
    if (ids.length > 0 && result.conflicts.includes(ids[0])) {
        return `${cmd}: ${kind} recorded, but it conflicts with the existing constraint set.`;
    }
    return `${cmd}: ${kind} constraint added.`;
}

/** Constraints touching any of the picked entities. */
function constraintIdsFor(entityIds: string[]): string[] {
    const set = new Set(entityIds);
    return useConstraintStore.getState().set.constraints
        .filter((c) => c.refs.some((r) => set.has(r.entityId)))
        .map((c) => c.id);
}

const pickOpts = (kind: keyof typeof KIND_ACCEPT_TYPES) => KIND_ACCEPT_TYPES[kind];

export const constraintRunners: RunnerMap = {
    // --------------------------------------------------------------
    // GEOMCONSTRAINT — the OCS distributor: type keyword -> GC command.
    // --------------------------------------------------------------
    'par.geom': () => {
        const kinds = ['Coincident', 'Horizontal', 'Vertical', 'Parallel', 'Perpendicular',
            'Tangent', 'Concentric', 'Equal', 'Fixed', 'PointOnCurve', 'Symmetric', 'Midpoint',
            'Colinear', 'Smooth'];
        runInteraction({
            steps: [{
                prompt: 'GEOMCONSTRAINT: constraint type',
                kind: 'text',
                options: kinds.map((k) => ({ keyword: k })),
            }],
            complete: (_p, values) => {
                const typed = String(values[0] ?? '').toLowerCase();
                const map: Record<string, string> = {
                    coincident: 'par.gcCoincident', horizontal: 'par.gcHorizontal', vertical: 'par.gcVertical',
                    parallel: 'par.gcParallel', perpendicular: 'par.gcPerpendicular', tangent: 'par.gcTangent',
                    concentric: 'par.gcConcentric', equal: 'par.gcEqual', fix: 'par.gcFix',
                    pointoncurve: 'par.gcPointOnCurve', symmetric: 'par.gcSymmetric', midpoint: 'par.gcMidpoint',
                    colinear: 'par.gcColinear', collinear: 'par.gcColinear', smooth: 'par.gcSmooth',
                };
                const action = map[typed];
                if (!action) return 'GEOMCONSTRAINT: unknown constraint type.';
                const handler = getRegisteredAction(action);
                if (!handler) return 'GEOMCONSTRAINT: constraint command unavailable.';
                const msg = handler();
                return typeof msg === 'string' ? msg : `GEOMCONSTRAINT: started ${action}`;
            },
        });
        return 'GEOMCONSTRAINT: pick a constraint type';
    },

    // --------------------------------------------------------------
    // Basic geometric constraints (Faz 6.4)
    // --------------------------------------------------------------
    'par.gcCoincident': () => {
        runInteraction({
            steps: [PK('GCCOINCIDENT: pick first point', pickOpts('Coincident')), PK('GCCOINCIDENT: pick second point', pickOpts('Coincident'))],
            complete: (_p, _v, picks, pickPoints) => {
                const pts = pickPoints ?? [];
                const a = pickRefAt(picks, pts, 0);
                const b = pickRefAt(picks, pts, 1);
                if (!a || !b) return 'GCCOINCIDENT: pick two pointable objects.';
                const { ids, result } = commitConstraints('Coincident', [{ kind: 'Coincident', refs: [a.ref, b.ref] }]);
                return reportGc('GCCOINCIDENT', 'Coincident', ids, result);
            },
        });
        return 'GCCOINCIDENT: pick first point';
    },

    'par.gcFix': () => {
        runInteraction({
            steps: [PK('GCFIX: pick an object to fix in place', pickOpts('Fixed'))],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const entity = entities[picks[0]];
                if (!entity) return 'GCFIX: invalid pick.';
                const markers = particleMarkers(entity);
                if (markers.length === 0) return 'GCFIX: object has no constraint points.';
                const refs: ConstraintRef[] = markers.map((m) => ({ entityId: entity.id, marker: m }));
                // Record the pinned points at constraint time: every solve
                // re-bases them here, so MOVE reverts instead of re-basing.
                const fixedPoints = markers.map((m) => {
                    const p = resolveMarkerPoint(entity, m);
                    return p ? [p[0], p[1], p[2] ?? 0] : [0, 0, 0];
                });
                const { ids, result } = commitConstraints('Fixed', [{ kind: 'Fixed', refs, fixedPoints }]);
                return reportGc('GCFIX', 'Fixed', ids, result);
            },
        });
        return 'GCFIX: pick an object';
    },

    'par.gcHorizontal': () => {
        runInteraction({
            steps: [PK('GCHORIZONTAL: pick a line/segment', pickOpts('Horizontal'))],
            complete: (_p, _v, picks, pickPoints) => {
                const { entities } = useAppStore.getState();
                const entity = entities[picks[0]];
                const wp = (pickPoints ?? [])[0];
                const refs = entity && wp ? segmentRefsOf(entity, [wp.x, wp.y]) : null;
                if (!refs) return 'GCHORIZONTAL: pick a line or a polyline segment.';
                const { ids, result } = commitConstraints('Horizontal', [{ kind: 'Horizontal', refs }]);
                return reportGc('GCHORIZONTAL', 'Horizontal', ids, result);
            },
        });
        return 'GCHORIZONTAL: pick a line';
    },

    'par.gcVertical': () => {
        runInteraction({
            steps: [PK('GCVERTICAL: pick a line/segment', pickOpts('Vertical'))],
            complete: (_p, _v, picks, pickPoints) => {
                const { entities } = useAppStore.getState();
                const entity = entities[picks[0]];
                const wp = (pickPoints ?? [])[0];
                const refs = entity && wp ? segmentRefsOf(entity, [wp.x, wp.y]) : null;
                if (!refs) return 'GCVERTICAL: pick a line or a polyline segment.';
                const { ids, result } = commitConstraints('Vertical', [{ kind: 'Vertical', refs }]);
                return reportGc('GCVERTICAL', 'Vertical', ids, result);
            },
        });
        return 'GCVERTICAL: pick a line';
    },

    // --------------------------------------------------------------
    // Remaining geometric constraints (Faz 6.5)
    // --------------------------------------------------------------
    'par.gcParallel': () => {
        runInteraction({
            steps: [PK('GCPARALLEL: pick first line', pickOpts('Parallel')), PK('GCPARALLEL: pick second line', pickOpts('Parallel'))],
            complete: (_p, _v, picks, pickPoints) => {
                const pts = pickPoints ?? [];
                const { entities } = useAppStore.getState();
                const r1 = entities[picks[0]] && pts[0] ? segmentRefsOf(entities[picks[0]], [pts[0].x, pts[0].y]) : null;
                const r2 = entities[picks[1]] && pts[1] ? segmentRefsOf(entities[picks[1]], [pts[1].x, pts[1].y]) : null;
                if (!r1 || !r2) return 'GCPARALLEL: pick two lines/segments.';
                const { ids, result } = commitConstraints('Parallel', [{ kind: 'Parallel', refs: [...r1, ...r2] }]);
                return reportGc('GCPARALLEL', 'Parallel', ids, result);
            },
        });
        return 'GCPARALLEL: pick first line';
    },

    'par.gcPerpendicular': () => {
        runInteraction({
            steps: [PK('GCPERPENDICULAR: pick first line', pickOpts('Perpendicular')), PK('GCPERPENDICULAR: pick second line', pickOpts('Perpendicular'))],
            complete: (_p, _v, picks, pickPoints) => {
                const pts = pickPoints ?? [];
                const { entities } = useAppStore.getState();
                const r1 = entities[picks[0]] && pts[0] ? segmentRefsOf(entities[picks[0]], [pts[0].x, pts[0].y]) : null;
                const r2 = entities[picks[1]] && pts[1] ? segmentRefsOf(entities[picks[1]], [pts[1].x, pts[1].y]) : null;
                if (!r1 || !r2) return 'GCPERPENDICULAR: pick two lines/segments.';
                const { ids, result } = commitConstraints('Perpendicular', [{ kind: 'Perpendicular', refs: [...r1, ...r2] }]);
                return reportGc('GCPERPENDICULAR', 'Perpendicular', ids, result);
            },
        });
        return 'GCPERPENDICULAR: pick first line';
    },

    'par.gcTangent': () => {
        runInteraction({
            steps: [PK('GCTANGENT: pick a line or circle/arc', pickOpts('Tangent')), PK('GCTANGENT: pick the second object', pickOpts('Tangent'))],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e1 = entities[picks[0]], e2 = entities[picks[1]];
                if (!e1 || !e2) return 'GCTANGENT: invalid picks.';
                const radial1 = RADIAL_TYPES.includes(e1.type), radial2 = RADIAL_TYPES.includes(e2.type);
                const line1 = LINE_TYPES.includes(e1.type), line2 = LINE_TYPES.includes(e2.type);
                const cRef = (e: Entity): ConstraintRef => ({ entityId: e.id, marker: MARKER_CENTER });
                const rRef = (e: Entity): ConstraintRef => ({ entityId: e.id, marker: MARKER_RADIUS });
                const segOf = (e: Entity): ConstraintRef[] | null => {
                    const markers = particleMarkers(e);
                    if (e.type === 'line') return [{ entityId: e.id, marker: 0 }, { entityId: e.id, marker: 1 }];
                    if (markers.length >= 2) return [{ entityId: e.id, marker: markers[0] }, { entityId: e.id, marker: markers[1] }];
                    return null;
                };
                let input: ConstraintInput | null = null;
                if (line1 && radial2) input = { kind: 'Tangent', refs: [...(segOf(e1) ?? []), cRef(e2), rRef(e2)], value: 0 };
                else if (line2 && radial1) input = { kind: 'Tangent', refs: [...(segOf(e2) ?? []), cRef(e1), rRef(e1)], value: 0 };
                else if (radial1 && radial2) input = { kind: 'Tangent', refs: [cRef(e1), rRef(e1), cRef(e2), rRef(e2)], value: -1 };
                if (!input) return 'GCTANGENT: pick a line + circle/arc, or two circles/arcs.';
                const { ids, result } = commitConstraints('Tangent', [input]);
                return reportGc('GCTANGENT', 'Tangent', ids, result);
            },
        });
        return 'GCTANGENT: pick a line or circle';
    },

    'par.gcEqual': () => {
        runInteraction({
            steps: [PK('GCEQUAL: pick first object', pickOpts('Equal')), PK('GCEQUAL: pick second object', pickOpts('Equal'))],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e1 = entities[picks[0]], e2 = entities[picks[1]];
                if (!e1 || !e2) return 'GCEQUAL: invalid picks.';
                const radial1 = RADIAL_TYPES.includes(e1.type), radial2 = RADIAL_TYPES.includes(e2.type);
                const cRef = (e: Entity): ConstraintRef => ({ entityId: e.id, marker: MARKER_CENTER });
                const rRef = (e: Entity): ConstraintRef => ({ entityId: e.id, marker: MARKER_RADIUS });
                const segOf = (e: Entity): ConstraintRef[] => {
                    if (e.type === 'line') return [{ entityId: e.id, marker: 0 }, { entityId: e.id, marker: 1 }];
                    const vs = (Array.isArray((e.data as Record<string, any>).vertices) ? (e.data as Record<string, any>).vertices : []) as Pt3[];
                    if (vs.length >= 2) return [{ entityId: e.id, marker: 0 }, { entityId: e.id, marker: 1 }];
                    return [];
                };
                let input: ConstraintInput | null = null;
                if (!radial1 && !radial2) {
                    const s1 = segOf(e1), s2 = segOf(e2);
                    if (s1.length < 2 || s2.length < 2) return 'GCEQUAL: pick two lines or two circles/arcs.';
                    input = { kind: 'Equal', refs: [...s1, ...s2], value: 0 };
                } else if (radial1 && radial2) {
                    input = { kind: 'Equal', refs: [cRef(e1), rRef(e1), cRef(e2), rRef(e2)], value: -1 };
                }
                if (!input) return 'GCEQUAL: pick two lines or two circles/arcs.';
                const { ids, result } = commitConstraints('Equal', [input]);
                return reportGc('GCEQUAL', 'Equal', ids, result);
            },
        });
        return 'GCEQUAL: pick first object';
    },

    'par.gcSymmetric': () => {
        runInteraction({
            steps: [
                PK('GCSYMMETRIC: pick first point', pickOpts('Symmetric')),
                PK('GCSYMMETRIC: pick second point', pickOpts('Symmetric')),
                PK('GCSYMMETRIC: pick the symmetry axis line', pickOpts('Symmetric')),
            ],
            complete: (_p, _v, picks, pickPoints) => {
                const pts = pickPoints ?? [];
                const a = pickRefAt(picks, pts, 0);
                const b = pickRefAt(picks, pts, 1);
                const { entities } = useAppStore.getState();
                const axis = entities[picks[2]];
                const axisRefs = axis && pts[2] ? segmentRefsOf(axis, [pts[2].x, pts[2].y]) : null;
                if (!a || !b || !axisRefs) return 'GCSYMMETRIC: pick two points and an axis line.';
                const { ids, result } = commitConstraints('Symmetric', [{ kind: 'Symmetric', refs: [a.ref, b.ref, ...axisRefs] }]);
                return reportGc('GCSYMMETRIC', 'Symmetric', ids, result);
            },
        });
        return 'GCSYMMETRIC: pick first point';
    },

    'par.gcMidpoint': () => {
        runInteraction({
            steps: [
                PK('GCMIDPOINT: pick a point', pickOpts('Midpoint')),
                PK('GCMIDPOINT: pick the target line/segment', pickOpts('Midpoint')),
            ],
            complete: (_p, _v, picks, pickPoints) => {
                const pts = pickPoints ?? [];
                const point = pickRefAt(picks, pts, 0);
                const { entities } = useAppStore.getState();
                const seg = entities[picks[1]];
                const segRefs = seg && pts[1] ? segmentRefsOf(seg, [pts[1].x, pts[1].y]) : null;
                if (!point || !segRefs) return 'GCMIDPOINT: pick a point and a line/segment.';
                const { ids, result } = commitConstraints('Midpoint', [{ kind: 'Midpoint', refs: [point.ref, ...segRefs] }]);
                return reportGc('GCMIDPOINT', 'Midpoint', ids, result);
            },
        });
        return 'GCMIDPOINT: pick a point';
    },

    'par.gcPointOnCurve': () => {
        runInteraction({
            steps: [
                PK('GCPOINTONCURVE: pick a point', pickOpts('PointOnCurve')),
                PK('GCPOINTONCURVE: pick the curve', pickOpts('PointOnCurve')),
            ],
            complete: (_p, _v, picks, pickPoints) => {
                const pts = pickPoints ?? [];
                const point = pickRefAt(picks, pts, 0);
                const { entities } = useAppStore.getState();
                const curve = entities[picks[1]];
                const wp = pts[1];
                if (!point || !curve || !wp) return 'GCPOINTONCURVE: pick a point and a curve.';
                let refs: ConstraintRef[] | null = null;
                let value = 0;
                if (curve.type === 'line') refs = [{ entityId: curve.id, marker: 0 }, { entityId: curve.id, marker: 1 }];
                else if (curve.type === 'polyline' || curve.type === 'polygon') {
                    const seg = nearestPolylineSegment(curve, [wp.x, wp.y]);
                    const vs = (Array.isArray((curve.data as Record<string, any>).vertices) ? (curve.data as Record<string, any>).vertices : []) as Pt3[];
                    if (seg !== null && vs.length > 0) {
                        refs = [{ entityId: curve.id, marker: seg }, { entityId: curve.id, marker: (seg + 1) % vs.length }];
                    }
                } else if (RADIAL_TYPES.includes(curve.type)) {
                    refs = [{ entityId: curve.id, marker: MARKER_CENTER }, { entityId: curve.id, marker: MARKER_RADIUS }];
                    value = -1;
                }
                if (!refs) return 'GCPOINTONCURVE: unsupported curve type.';
                const { ids, result } = commitConstraints('PointOnCurve', [{ kind: 'PointOnCurve', refs, value }]);
                return reportGc('GCPOINTONCURVE', 'PointOnCurve', ids, result);
            },
        });
        return 'GCPOINTONCURVE: pick a point';
    },

    'par.gcSmooth': () => {
        runInteraction({
            steps: [PK('GCSMOOTH: pick first curve end', pickOpts('Smooth')), PK('GCSMOOTH: pick second curve end', pickOpts('Smooth'))],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e1 = entities[picks[0]], e2 = entities[picks[1]];
                if (!e1 || !e2) return 'GCSMOOTH: invalid picks.';
                // Recorded stub (Faz 6.5): curvature-continuity enforcement
                // needs the spline kernel; the record keeps the UI honest.
                const { ids } = commitConstraints('Smooth', [{ kind: 'Smooth', refs: [{ entityId: e1.id, marker: 0 }, { entityId: e2.id, marker: 0 }] }]);
                return `GCSMOOTH: Smooth constraint recorded on ${ids[0]} (curvature solving lands with the spline kernel).`;
            },
        });
        return 'GCSMOOTH: pick first curve end';
    },

    'par.gcConcentric': () => {
        runInteraction({
            steps: [PK('GCCONCENTRIC: pick first circle/arc', pickOpts('Concentric')), PK('GCCONCENTRIC: pick second circle/arc', pickOpts('Concentric'))],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e1 = entities[picks[0]], e2 = entities[picks[1]];
                if (!e1 || !e2 || !RADIAL_TYPES.includes(e1.type) || !RADIAL_TYPES.includes(e2.type)) {
                    return 'GCCONCENTRIC: pick two circles/arcs.';
                }
                const refs: ConstraintRef[] = [
                    { entityId: e1.id, marker: MARKER_CENTER },
                    { entityId: e2.id, marker: MARKER_CENTER },
                ];
                const { ids, result } = commitConstraints('Concentric', [{ kind: 'Concentric', refs }]);
                return reportGc('GCCONCENTRIC', 'Concentric', ids, result);
            },
        });
        return 'GCCONCENTRIC: pick first circle';
    },

    'par.gcColinear': () => {
        runInteraction({
            steps: [PK('GCCOLINEAR: pick first line', pickOpts('Colinear')), PK('GCCOLINEAR: pick second line', pickOpts('Colinear'))],
            complete: (_p, _v, picks, pickPoints) => {
                const pts = pickPoints ?? [];
                const { entities } = useAppStore.getState();
                const r1 = entities[picks[0]] && pts[0] ? segmentRefsOf(entities[picks[0]], [pts[0].x, pts[0].y]) : null;
                const r2 = entities[picks[1]] && pts[1] ? segmentRefsOf(entities[picks[1]], [pts[1].x, pts[1].y]) : null;
                if (!r1 || !r2) return 'GCCOLINEAR: pick two lines/segments.';
                const { ids, result } = commitConstraints('Colinear', [{ kind: 'Colinear', refs: [...r1, ...r2] }]);
                return reportGc('GCCOLINEAR', 'Colinear', ids, result);
            },
        });
        return 'GCCOLINEAR: pick first line';
    },

    // --------------------------------------------------------------
    // AUTOCONSTRAIN (Faz 6.6)
    // --------------------------------------------------------------
    'par.autoconstrain': () => {
        runInteraction({
            steps: [PLoopK('AUTOCONSTRAIN: select objects (Enter = whole drawing)', 0)],
            complete: (_p, _v, picks) => {
                const settings = useConstraintStore.getState().autoConstrain;
                const { inputs, skipped, scanned } = inferAutoConstrain(picks, settings);
                if (inputs.length === 0) {
                    return `AUTOCONSTRAIN: no constraints inferred (${scanned} object(s) scanned, ${skipped} duplicate(s) skipped).`;
                }
                undoableConstraints(() => {
                    useConstraintStore.getState().addConstraints(inputs);
                }, 'AUTOCONSTRAIN');
                const result = solveConstraintSet();
                if (result.converged || result.conflicts.length > 0) syncBoundDimensions();
                const kinds = inputs.reduce<Record<string, number>>((acc, i) => {
                    acc[i.kind] = (acc[i.kind] ?? 0) + 1;
                    return acc;
                }, {});
                const breakdown = Object.entries(kinds).map(([k, n]) => `${k} ${n}`).join(', ');
                const eqNote = settings.equalEnabled ? '' : ' — Equal inference is off (enable it in the AUTOCONSTRAIN settings dialog)';
                return `AUTOCONSTRAIN: ${inputs.length} constraint(s) added (${breakdown}); ${skipped} duplicate(s) skipped${eqNote}${result.conflicts.length ? `; ${result.conflicts.length} conflicting` : ''}.`;
            },
        });
        return 'AUTOCONSTRAIN: select objects (Enter = whole drawing)';
    },

    // --------------------------------------------------------------
    // DELCONSTRAINT + visibility (Faz 6.7)
    // --------------------------------------------------------------
    'par.delConstraint': () => {
        const selected = useConstraintStore.getState().selectedConstraintId;
        if (selected) {
            undoableConstraints(() => {
                useConstraintStore.getState().removeConstraints([selected]);
            }, 'DELCONSTRAINT');
            return `DELCONSTRAINT: constraint ${selected} removed.`;
        }
        runInteraction({
            steps: [PK('DELCONSTRAINT: pick an object to drop its constraints')],
            complete: (_p, _v, picks) => {
                const ids = constraintIdsFor(picks);
                if (ids.length === 0) return 'DELCONSTRAINT: the picked object has no constraints.';
                undoableConstraints(() => {
                    useConstraintStore.getState().removeConstraints(ids);
                }, 'DELCONSTRAINT');
                return `DELCONSTRAINT: ${ids.length} constraint(s) removed.`;
            },
        });
        return 'DELCONSTRAINT: pick an object';
    },

    'par.gcShow': () => {
        runInteraction({
            steps: [PK('GCSHOW: pick an object to reveal its constraint glyphs')],
            complete: (_p, _v, picks) => {
                const ids = constraintIdsFor(picks);
                if (ids.length === 0) return 'GCSHOW: no constraints on the picked object.';
                undoableConstraints(() => {
                    useConstraintStore.getState().setConstraintsHidden(ids, false);
                }, 'GCSHOW');
                return `GCSHOW: ${ids.length} constraint(s) shown.`;
            },
        });
        return 'GCSHOW: pick an object';
    },

    'par.gcHide': () => {
        runInteraction({
            steps: [PK('GCHIDE: pick an object to hide its constraint glyphs')],
            complete: (_p, _v, picks) => {
                const ids = constraintIdsFor(picks);
                if (ids.length === 0) return 'GCHIDE: no constraints on the picked object.';
                undoableConstraints(() => {
                    useConstraintStore.getState().setConstraintsHidden(ids, true);
                }, 'GCHIDE');
                return `GCHIDE: ${ids.length} constraint(s) hidden.`;
            },
        });
        return 'GCHIDE: pick an object';
    },

    'par.gcShowAll': () => {
        undoableConstraints(() => {
            useConstraintStore.getState().setAllConstraintsHidden(false);
        }, 'GCSHOWALL');
        return 'GCSHOWALL: all constraint glyphs shown.';
    },

    'par.gcHideAll': () => {
        undoableConstraints(() => {
            useConstraintStore.getState().setAllConstraintsHidden(true);
        }, 'GCHIDEALL');
        return 'GCHIDEALL: all constraint glyphs hidden.';
    },

    // CONSTRAINTBAR (OCS Show/Hide/Reset distributor).
    'par.constraintBar': () => {
        runInteraction({
            steps: [{
                prompt: 'CONSTRAINTBAR: choose an action',
                kind: 'text',
                options: [{ keyword: 'Show' }, { keyword: 'Hide' }, { keyword: 'Reset' }],
            }],
            complete: (_p, values) => {
                const action = String(values[0] ?? '').toLowerCase();
                if (action === 'show') {
                    undoableConstraints(() => useConstraintStore.getState().setAllConstraintsHidden(false), 'CONSTRAINTBAR Show');
                    return 'CONSTRAINTBAR: all constraint glyphs shown.';
                }
                if (action === 'hide') {
                    undoableConstraints(() => useConstraintStore.getState().setAllConstraintsHidden(true), 'CONSTRAINTBAR Hide');
                    return 'CONSTRAINTBAR: all constraint glyphs hidden.';
                }
                if (action === 'reset') {
                    const result = solveConstraintSet();
                    if (result.converged || result.conflicts.length > 0) syncBoundDimensions();
                    return result.converged
                        ? 'CONSTRAINTBAR: constraint set re-solved.'
                        : `CONSTRAINTBAR: re-solve left ${result.conflicts.length} conflict(s).`;
                }
                return 'CONSTRAINTBAR: unknown option (Show/Hide/Reset).';
            },
        });
        return 'CONSTRAINTBAR: Show / Hide / Reset';
    },

    // Dialogs live on the manage surface (Faz 6.6 / 6.9).
    'par.autoconstrainSettings': () => {
        useAppStore.getState().openManageDialog('autoConstrain');
        return 'AUTOCONSTRAIN: settings dialog opened.';
    },
};
